import { Plus } from "lucide-react";
import { useState } from "react";
import { useCreate, useGetList, useNotify, useRefresh } from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { getActiveWorkspaceId } from "../workspace/activeWorkspace";

interface ContactChoice {
  id: number;
  first_name: string;
  last_name: string;
}

const inputClass =
  "h-9 w-full rounded-md border border-input bg-transparent px-2.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring";

export const NewAppointmentDialog = () => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [contactId, setContactId] = useState<string>("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState("09:00");
  const [duration, setDuration] = useState("60");
  const [notes, setNotes] = useState("");
  const [create, { isPending }] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();

  const { data: contacts } = useGetList<ContactChoice>("contacts_summary", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "first_name", order: "ASC" },
  });

  const submit = async () => {
    if (!title.trim()) {
      notify("A title is required", { type: "warning" });
      return;
    }
    const startsAt = new Date(`${date}T${time}:00`);
    const endsAt = new Date(startsAt.getTime() + Number(duration) * 60_000);
    // The identity's sales id (assign to me by default).
    const {
      data: { user },
    } = await getSupabaseClient().auth.getUser();
    const { data: sale } = await getSupabaseClient()
      .from("sales")
      .select("id")
      .eq("user_id", user?.id ?? "")
      .single();

    create(
      "appointments",
      {
        data: {
          title: title.trim(),
          contact_id: contactId ? Number(contactId) : null,
          assigned_to: sale?.id ?? null,
          starts_at: startsAt.toISOString(),
          ends_at: endsAt.toISOString(),
          notes: notes.trim() || null,
          workspace_id: getActiveWorkspaceId(),
        },
      },
      {
        mutationMode: "pessimistic",
        onSuccess: () => {
          notify("Appointment scheduled");
          setOpen(false);
          setTitle("");
          setNotes("");
          refresh();
        },
        onError: (error: unknown) =>
          notify(
            error instanceof Error
              ? error.message
              : "Could not create the appointment",
            { type: "error" },
          ),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="mr-1 size-3.5" />
          New Appointment
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>New appointment</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            Title
            <input
              className={inputClass}
              placeholder="Site survey with…"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            Contact (optional)
            <select
              className={inputClass}
              value={contactId}
              onChange={(e) => setContactId(e.target.value)}
            >
              <option value="">No contact</option>
              {(contacts ?? []).map((contact) => (
                <option key={contact.id} value={contact.id}>
                  {contact.first_name} {contact.last_name}
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-3 gap-2">
            <label className="col-span-1 flex flex-col gap-1 text-[12.5px] font-medium">
              Date
              <input
                className={inputClass}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 text-[12.5px] font-medium">
              Start
              <input
                className={inputClass}
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 text-[12.5px] font-medium">
              Minutes
              <input
                className={inputClass}
                type="number"
                min="15"
                step="15"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              />
            </label>
          </div>
          <label className="flex flex-col gap-1 text-[12.5px] font-medium">
            Notes (optional)
            <textarea
              className="min-h-16 w-full rounded-md border border-input bg-transparent px-2.5 py-1.5 text-[13px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button disabled={isPending} onClick={submit}>
              {isPending ? "Scheduling…" : "Schedule"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
